import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { ActivityNode } from './activityplanning.models';

import { planningNodeStatus, planningNodeSx} from './ActivityTreePanelHelper';


type Props = {
  activities: ActivityNode[];
  selectedNid?: string | null;
  onSelect: (node: ActivityNode) => void;
};

type State = {
  mode: string;
  search: string;
};

const modes = [
  { value: 'KPI', label: 'KPI' },
  { value: 'ACTIVITY', label: 'Activities' },
  { value: 'TASK', label: 'Tasks' },
  { value: 'OBJECTIVE', label: 'Objectives' },
  { value: 'ALL', label: 'All' },
];

const nodeColor = (type: string) => {
  switch (type) {
    case 'AXIS': return 'primary';
    case 'GLOBAL_OBJECTIVE':
    case 'SPECIFIC_OBJECTIVE':
    case 'OBJECTIVE': return 'warning';
    case 'KPI': return 'success';
    case 'ACTIVITY': return 'secondary';
    case 'TASK': return 'default';
    default: return 'default';
  }
};

const indent = (level?: number) => Math.max(0, Number(level || 1) - 1) * 12;

class ActivityTreePanel extends React.Component<Props, State> {
  state: State = {
    mode: 'KPI',
    search: '',
  };

  setMode = (_: any, mode: string) => {
    this.setState({ mode });
  };

  setSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: e.target.value });
  };

  filteredRows = () => {
    const { activities } = this.props;
    const { mode, search } = this.state;
    const q = search.trim().toUpperCase();

    return activities.filter((node) => {
      const nodeType = String(node.nodetype || '').toUpperCase();

      const modeOk =
        mode === 'ALL' ||
        nodeType === mode ||
        (mode === 'OBJECTIVE' && ['OBJECTIVE', 'GLOBAL_OBJECTIVE', 'SPECIFIC_OBJECTIVE'].includes(nodeType));

      if (!modeOk) return false;

      if (!q) return true;

      const haystack = [
        node.code,
        node.name,
        node.axiscode,
        node.axisname,
        node.objectivecode,
        node.objectivename,
        node.costcentercode,
        node.costcentername,
        node.description,
      ].join(' ').toUpperCase();

      return haystack.includes(q);
    });
  };

  renderCard = (node: ActivityNode) => {
    const selected = node.nid === this.props.selectedNid;

    return (
      <Card
        key={node.nid}
        variant={selected ? 'elevation' : 'outlined'}
        elevation={selected ? 3 : 0}
        sx={{
          mb: 1,
          ml: `${indent(node.levelno)}px`,
          borderColor: selected ? 'primary.main' : undefined,
          bgcolor: selected ? 'action.selected' : undefined,
          ...planningNodeSx(node, selected)
        }}
      >
        <CardActionArea onClick={() => this.props.onSelect(node)}>
          <CardContent sx={{ p: 1.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" color={nodeColor(node.nodetype) as any} label={node.nodetype} />
                  <Typography variant="caption" color="text.secondary">{node.code}</Typography>
                </Stack>

                <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap title={node.name}>
                  {node.name}
                </Typography>

                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {node.axiscode || ''} {node.objectivecode || ''} {node.costcentercode ? `• ${node.costcentercode}` : ''}
                </Typography>
              </Box>

              <Chip size="small" label={node.status || '1'} variant="outlined" />
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  render() {
    const rows = this.filteredRows();

    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Planning Tree</Typography>
          <Typography variant="caption" color="text.secondary">
            {rows.length} / {this.props.activities.length}
          </Typography>
        </Stack>

        <Tabs
          value={this.state.mode}
          onChange={this.setMode}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1 }}
        >
          {modes.map((m) => (
            <Tab key={m.value} value={m.value} label={m.label} />
          ))}
        </Tabs>

        <TextField
          size="small"
          label="Search tree"
          value={this.state.search}
          onChange={this.setSearch}
          fullWidth
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ maxHeight: 700, overflow: 'auto', pr: 0.5 }}>
          {rows.map(this.renderCard)}

          {rows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No matching planning nodes found.
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
}

export default ActivityTreePanel;
